import { forwardRef } from "react";
import { Reveal, Eyebrow } from "./Reveal";
import { ARTICLES } from "../data/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const InsightCard = forwardRef(({ article, ...props }, ref) => (
  <button
    ref={ref}
    {...props}
    data-testid={`insight-card-${article.id}`}
    className="group w-full text-left bg-white rounded-2xl border border-[#0B2A5B]/10 overflow-hidden h-full flex flex-col hover:border-[#1E5FE0]/40 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(11,42,91,0.3)] transition-all duration-500"
  >
    <div className="aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
      <img
        src={article.image}
        alt={article.title}
        data-testid={`insight-image-${article.id}`}
        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
      />
    </div>
    <div className="p-8 lg:p-10 flex flex-col flex-1">
      <div className="flex items-center justify-between">
        <span className="f-label text-xs uppercase tracking-[0.2em] text-[#1E5FE0]">Synergy Next — Perspectives</span>
        <ArrowUpRight className="w-5 h-5 text-[#0B2A5B]/30 group-hover:text-[#1E5FE0] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
      </div>
      <h3 className="f-display text-xl lg:text-2xl text-[#0B2A5B] mt-5 leading-snug">{article.title}</h3>
      <p className="mt-4 text-sm leading-relaxed text-slate-600 flex-1">{article.excerpt}</p>
      <span className="mt-6 f-label text-[13px] uppercase tracking-[0.2em] text-[#1E5FE0]">Read the essay &rarr;</span>
    </div>
  </button>
));
InsightCard.displayName = "InsightCard";

const InsightModal = ({ article }) => (
  <DialogContent
    data-testid={`insight-modal-${article.id}`}
    className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FAFAF5] border-[#0B2A5B]/15"
  >
    <DialogHeader>
      <p className="f-label text-xs uppercase tracking-[0.2em] text-[#1E5FE0] mb-3">Synergy Next — Perspectives</p>
      <DialogTitle className="f-display text-2xl lg:text-3xl text-[#0B2A5B] leading-snug">{article.title}</DialogTitle>
    </DialogHeader>
    <div className="mt-4 space-y-5">
      {article.paragraphs.map((p) => (
        <p key={p} className="text-[15px] leading-[1.85] text-slate-700">{p}</p>
      ))}
    </div>
    <Link
      to={`/insights/${article.id}`}
      data-testid={`insight-open-page-${article.id}`}
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0B2A5B] text-white px-5 py-3 f-label text-[13px] uppercase tracking-[0.15em] hover:bg-[#1E5FE0] transition-colors self-start"
    >
      Open full article & share <ArrowUpRight className="w-4 h-4" />
    </Link>
  </DialogContent>
);

export const Insights = () => (
  <section id="insights" data-testid="insights-section" className="py-24 lg:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Our Insights</Eyebrow>
        <h2 className="f-display text-4xl sm:text-5xl lg:text-[3.6rem] text-[#0B2A5B] tracking-tight mt-4 max-w-3xl leading-[1.1]">
          Thinking that moves <span className="italic text-[#1E5FE0]">organisations forward.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base text-slate-600 leading-relaxed">
          Perspectives on strategy, governance, leadership and institution-building —
          drawn from three decades inside Indian organisations.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
        {ARTICLES.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.07}>
            <Dialog>
              <DialogTrigger asChild>
                <InsightCard article={a} />
              </DialogTrigger>
              <InsightModal article={a} />
            </Dialog>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
